/**
 * Tank registry PR 2 — "Operator tanks": Paelode keeps an operator company's tank registry on its
 * behalf, the same as the operator admin's Tank setup in the portal (web-portal
 * routes/TankRegistrySections.tsx): the tanks with placement, sizes, tags and each linked
 * hauler's own sizes; add and edit tanks; tags; batteries and their wells; and a CSV / XLSX
 * import (OperatorTankImport.tsx). Hauler proposals and links stay the company's own.
 *
 * Corbin's rules (13.8, D-20/D-22): a tank's PRODUCER TANK ID (optional, e.g. a lease tank
 * number) identifies it, unique across the company among active tanks; machine readings use it.
 * Its NAME is unique only at its well, site or battery: names repeat across wells. A tank
 * sits on exactly one of a catalog well, a free-text site or one of the company's batteries. The
 * company's sizes and each hauler's are kept apart; neither overwrites the other, so a hauler's
 * sizes show next to the company's, marked when they differ. "Needs size": a reading was given in
 * inches and the tank has no bbl per inch to turn it into barrels.
 *
 * DEC-379: the form offers the active standard tank sizes, each labelled with its size, so the
 * two 500 BBL tanks are separate choices; picking one fills capacity, height and bbl per inch and
 * links the tank. A capacity with more than one standard size, no size picked and no bbl per
 * inch asks the user to pick one; it is never guessed (DEC-377).
 */
import * as React from 'react';
import { standardTankLabel, suggestBblPerInch } from '@lodex-pro/shared/tank-strapping';
import { useStandardTanks } from '../tank-catalog/hooks';
import {
  TANK_TAG_SYSTEMS,
  type BatteryView,
  type CompanyWellOption,
  type RegistryTankLinkView,
  type RegistryTankTagView,
  type RegistryTankView,
  type TankTagSystem,
} from '@lodex-pro/shared/validation/operator-portal-routes';
import { Button } from 'src/components/ui/button';
import { Pill, fieldStyle, labelStyle, subText } from '../../ui/bits';
import {
  tankRegistryErrorMessage,
  useAddCompanyTankTag,
  useCompanyBatteries,
  useCompanyTanks,
  useCompanyWellOptions,
  useRemoveCompanyTankTag,
  useSaveCompanyBattery,
  useSaveCompanyTank,
  useSetCompanyBatteryWells,
} from './tank-registry-hooks';
import { OperatorTankImport } from './OperatorTankImport';
import { formatNumber } from '@lodex-pro/shared/number-format';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '20px',
  marginBottom: '16px',
};
const itemStyle: React.CSSProperties = {
  borderRadius: '12px',
  backgroundColor: 'var(--color-bg-tertiary)',
  padding: '10px 12px',
};
const primaryButton: React.CSSProperties = {
  backgroundColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const errorText: React.CSSProperties = {
  ...labelStyle,
  textTransform: 'none',
  color: 'var(--color-danger)',
  margin: '6px 0 0',
};
const heading: React.CSSProperties = { fontSize: '13px', fontWeight: 600, margin: '18px 0 8px' };

// ── Labels (small copies of the portal's; packages do not import each other) ─

function wellLabel(w: {
  readonly wellName: string | null;
  readonly apiNumber: string | null;
}): string {
  const name = w.wellName !== null && w.wellName.trim() !== '' ? w.wellName : 'Unnamed well';
  return w.apiNumber !== null && w.apiNumber !== '' ? `${name} · API ${w.apiNumber}` : name;
}

function placeLabel(t: RegistryTankView): string {
  if (t.wellId !== null) return wellLabel(t);
  if (t.batteryId !== null) return `Battery: ${t.batteryName ?? 'unnamed'}`;
  if (t.siteLabel !== null && t.siteLabel.trim() !== '') return `Site: ${t.siteLabel}`;
  return 'No well, battery or site';
}

/** A decimal string from the API as a plain number, or null when not set. */
function decimal(v: string | null): string | null {
  if (v === null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? v : n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function sizesDiffer(a: string | null, b: string | null): boolean {
  if (a === null || b === null || a === '' || b === '') return false;
  const x = Number(a);
  const y = Number(b);
  return Number.isFinite(x) && Number.isFinite(y) && x !== y;
}

const SYSTEM_LABEL: Record<TankTagSystem, string> = {
  SCADA: 'SCADA',
  MQTT: 'MQTT',
  OTHER: 'Other',
};

const sortWells = (wells: readonly CompanyWellOption[]): CompanyWellOption[] =>
  [...wells].sort((a, b) => wellLabel(a).localeCompare(wellLabel(b)));

// ── The card ─────────────────────────────────────────────────────────────────

export function OperatorTanksCard({ tenantId }: { tenantId: string }): React.JSX.Element {
  const tanks = useCompanyTanks(tenantId);
  const batteries = useCompanyBatteries(tenantId);
  const wells = useCompanyWellOptions(tenantId);
  const [adding, setAdding] = React.useState(false);
  const tankList = [...(tanks.data?.tanks ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const wellList = wells.data?.wells ?? [];
  const batteryList = batteries.data?.batteries ?? [];

  return (
    <section style={cardStyle} data-testid="operator-tanks-card" aria-label="Operator tanks">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 6px' }}>Operator tanks</h3>
        {!adding && (
          <Button type="button" variant="outline" size="sm" onClick={(): void => setAdding(true)}>
            Add tank
          </Button>
        )}
      </div>
      <p style={{ ...subText, textTransform: 'none', margin: '0 0 12px' }}>
        The company&rsquo;s own record of each tank: its name, its producer tank ID, where it sits,
        its sizes and its tags. Each hauler keeps its own sizes; neither overwrites the other.
      </p>
      {adding && (
        <div style={{ ...itemStyle, marginBottom: '12px' }}>
          <TankForm
            tenantId={tenantId}
            tank={null}
            wells={wellList}
            batteries={batteryList}
            onDone={(): void => setAdding(false)}
          />
        </div>
      )}
      {tanks.isLoading ? (
        <p style={subText}>Loading tanks…</p>
      ) : tanks.isError ? (
        <p role="alert" style={errorText}>
          Could not load the tanks.
        </p>
      ) : tankList.length === 0 ? (
        <p style={{ ...subText, textTransform: 'none' }}>No tanks yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {tankList.map((t) => (
            <TankItem
              key={t.id}
              tenantId={tenantId}
              tank={t}
              wells={wellList}
              batteries={batteryList}
            />
          ))}
        </div>
      )}
      <Batteries
        tenantId={tenantId}
        batteries={batteryList}
        loading={batteries.isLoading}
        failed={batteries.isError}
        wells={wellList}
      />
      <OperatorTankImport tenantId={tenantId} />
    </section>
  );
}

function TankItem({
  tenantId,
  tank: t,
  wells,
  batteries,
}: {
  tenantId: string;
  tank: RegistryTankView;
  wells: readonly CompanyWellOption[];
  batteries: readonly BatteryView[];
}): React.JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const capacity = decimal(t.capacityBbl);
  const height = decimal(t.heightIn);
  const bpi = decimal(t.bblPerInch);
  return (
    <article aria-label={`Tank ${t.name}`} style={{ ...itemStyle, opacity: t.isActive ? 1 : 0.75 }}>
      {editing ? (
        <TankForm
          tenantId={tenantId}
          tank={t}
          wells={wells}
          batteries={batteries}
          onDone={(): void => setEditing(false)}
        />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '14px' }}>{t.name}</strong>
            <span style={subText}>{placeLabel(t)}</span>
            {t.producerTankId != null && (
              <span style={{ fontSize: '13px' }} data-testid="producer-tank-id">
                Producer tank ID {t.producerTankId}
              </span>
            )}
            <Pill
              label={t.isActive ? 'Active' : 'Inactive'}
              tone={t.isActive ? 'positive' : 'muted'}
            />
            {t.needsSize && <Pill label="Needs size" tone="warning" />}
            <Button
              type="button"
              variant="outline"
              size="sm"
              style={{ marginLeft: 'auto' }}
              aria-label={`Edit ${t.name}`}
              onClick={(): void => setEditing(true)}
            >
              Edit
            </Button>
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px' }}>
            <span>
              <span style={subText}>Capacity</span> {capacity === null ? '—' : `${capacity} BBL`}
            </span>
            <span>
              <span style={subText}>Height</span> {height === null ? '—' : `${height} in`}
            </span>
            <span>
              <span style={subText}>BBL per inch</span> {bpi ?? '—'}
            </span>
            <span>
              <span style={subText}>Type</span> {t.tankType ?? '—'}
            </span>
          </div>
        </>
      )}
      <Tags tenantId={tenantId} tank={t} />
      {t.links.length === 0 ? (
        <p style={{ ...subText, textTransform: 'none', margin: '6px 0 0' }}>
          No hauler records linked to this tank.
        </p>
      ) : (
        <ul
          aria-label={`Haulers' records of ${t.name}`}
          style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, fontSize: '13px' }}
        >
          {t.links.map((l) => (
            <HaulerLink key={l.haulerTankId} tank={t} link={l} />
          ))}
        </ul>
      )}
    </article>
  );
}

function HaulerLink({
  tank,
  link: l,
}: {
  tank: RegistryTankView;
  link: RegistryTankLinkView;
}): React.JSX.Element {
  const cap = decimal(l.capacityBbl);
  const bpi = decimal(l.bblPerInch);
  const differs = [
    sizesDiffer(l.capacityBbl, tank.capacityBbl) ? 'capacity' : null,
    sizesDiffer(l.bblPerInch, tank.bblPerInch) ? 'BBL/in' : null,
  ].filter((x) => x !== null);
  return (
    <li style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', padding: '3px 0' }}>
      <span>{l.haulerName}</span>
      <span style={subText}>calls it “{l.haulerTankName}”</span>
      <span aria-label={`${l.haulerName}'s sizes`} style={subText}>
        their sizes: {cap === null ? 'capacity not set' : `${cap} BBL`} ·{' '}
        {bpi === null ? 'BBL per inch not set' : `${bpi} BBL/in`}
      </span>
      {differs.length > 0 && <Pill label={`differs: ${differs.join(', ')}`} tone="warning" />}
    </li>
  );
}

// ── Add / edit ───────────────────────────────────────────────────────────────

type Placement = 'WELL' | 'SITE' | 'BATTERY';

/** '' is "not set"; anything else must be a number above 0 and at most `max`. */
function parseSize(raw: string, max: number): { ok: true; value: number | null } | { ok: false } {
  const s = raw.trim();
  if (s === '') return { ok: true, value: null };
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0 || n > max) return { ok: false };
  return { ok: true, value: n };
}

const sizeText = (v: string | null): string => (v === null || v === '' ? '' : String(Number(v)));

function TankForm({
  tenantId,
  tank,
  wells,
  batteries,
  onDone,
}: {
  tenantId: string;
  /** null = a new tank. */
  tank: RegistryTankView | null;
  wells: readonly CompanyWellOption[];
  batteries: readonly BatteryView[];
  onDone: () => void;
}): React.JSX.Element {
  const save = useSaveCompanyTank(tenantId, tank?.id ?? null);
  const [name, setName] = React.useState(tank?.name ?? '');
  // 13.8: sent only when changed, so an untouched field keeps the stored ID (the API's rule).
  const [producerTankId, setProducerTankId] = React.useState(tank?.producerTankId ?? '');
  const [placement, setPlacement] = React.useState<Placement>(
    tank === null
      ? wells.length > 0
        ? 'WELL'
        : 'SITE'
      : tank.wellId !== null
        ? 'WELL'
        : tank.batteryId !== null
          ? 'BATTERY'
          : 'SITE',
  );
  const [wellId, setWellId] = React.useState(tank?.wellId ?? '');
  const [siteLabel, setSiteLabel] = React.useState(tank?.siteLabel ?? '');
  const [batteryId, setBatteryId] = React.useState(tank?.batteryId ?? '');
  const [capacity, setCapacity] = React.useState(sizeText(tank?.capacityBbl ?? null));
  const [height, setHeight] = React.useState(sizeText(tank?.heightIn ?? null));
  const [bpi, setBpi] = React.useState(sizeText(tank?.bblPerInch ?? null));
  const [tankType, setTankType] = React.useState(tank?.tankType ?? '');
  const [standardId, setStandardId] = React.useState(tank?.standardTankCatalogId ?? '');
  const [isActive, setIsActive] = React.useState(tank?.isActive ?? true);
  const allSizes = useStandardTanks().data?.standardTanks;
  const sizeOptions = React.useMemo(() => (allSizes ?? []).filter((t) => t.isActive), [allSizes]);

  const pickSize = (id: string): void => {
    setStandardId(id);
    const o = sizeOptions.find((x) => x.id === id);
    if (o === undefined) return;
    setCapacity(sizeText(o.capacityBbl));
    setBpi(sizeText(o.bblPerInch));
    if (o.heightFt !== null) setHeight(String(Math.round(Number(o.heightFt) * 12 * 100) / 100));
  };
  // A capacity with more than one standard size, no size picked and no bbl per inch: ask.
  const sizesAtCapacity =
    standardId === '' && bpi.trim() === ''
      ? suggestBblPerInch({ capacityBbl: capacity.trim() }, sizeOptions).choices
      : [];
  const [error, setError] = React.useState<string | null>(null);
  const idp = tank?.id ?? 'new';

  // An inactive battery stays choosable only for the tank already on it.
  const batteryOptions = batteries.filter((b) => b.isActive || b.id === tank?.batteryId);

  const submit = (): void => {
    setError(null);
    const nextName = name.trim();
    const site = siteLabel.trim();
    if (nextName === '') return setError('Give the tank a name.');
    if (placement === 'WELL' && wellId === '') return setError('Choose the well the tank sits on.');
    if (placement === 'SITE' && site === '') return setError('Name the site the tank sits on.');
    if (placement === 'BATTERY' && batteryId === '') {
      return setError('Choose the battery the tank sits on.');
    }
    const cap = parseSize(capacity, 100000);
    const h = parseSize(height, 2000);
    const b = parseSize(bpi, 1000);
    if (!cap.ok) return setError('Capacity must be a number of barrels above 0, or left empty.');
    if (!h.ok) return setError('Height must be a number of inches above 0, or left empty.');
    if (!b.ok) return setError('BBL per inch must be a number above 0, or left empty.');
    const type = tankType.trim();
    const pid = producerTankId.trim();
    const pidChanged =
      tank === null ? pid !== '' : pid.toUpperCase() !== (tank.producerTankId ?? '').toUpperCase();
    save.mutate(
      {
        name: nextName,
        ...(pidChanged ? { producerTankId: pid === '' ? null : pid } : {}),
        // Exactly one placement; the other two are cleared (an edit is the full record).
        wellId: placement === 'WELL' ? wellId : null,
        siteLabel: placement === 'SITE' ? site : null,
        batteryId: placement === 'BATTERY' ? batteryId : null,
        capacityBbl: cap.value,
        heightIn: h.value,
        bblPerInch: b.value,
        tankType: type === '' ? null : type,
        standardTankCatalogId: standardId === '' ? null : standardId,
        ...(tank === null ? {} : { isActive }),
      },
      { onSuccess: onDone, onError: (err) => setError(tankRegistryErrorMessage(err)) },
    );
  };

  const sizeField = (
    id: string,
    label: string,
    value: string,
    set: (v: string) => void,
  ): React.JSX.Element => (
    <div>
      <label htmlFor={`${id}-${idp}`} style={labelStyle}>
        {label}
      </label>
      <input
        id={`${id}-${idp}`}
        inputMode="decimal"
        value={value}
        onChange={(e): void => set(e.target.value)}
        style={{ ...fieldStyle, width: '130px' }}
      />
    </div>
  );

  return (
    <form
      aria-label={tank === null ? 'Add a tank' : `Edit ${tank.name}`}
      onSubmit={(e): void => {
        e.preventDefault();
        submit();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
    >
      <div>
        <label htmlFor={`tank-name-${idp}`} style={labelStyle}>
          Tank name (unique at its well, site or battery)
        </label>
        <input
          id={`tank-name-${idp}`}
          value={name}
          maxLength={120}
          onChange={(e): void => setName(e.target.value)}
          style={{ ...fieldStyle, maxWidth: '320px' }}
        />
      </div>
      <div>
        <label htmlFor={`tank-pid-${idp}`} style={labelStyle}>
          Producer tank ID (optional)
        </label>
        <input
          id={`tank-pid-${idp}`}
          aria-describedby={`tank-pid-help-${idp}`}
          value={producerTankId}
          maxLength={120}
          onChange={(e): void => setProducerTankId(e.target.value)}
          style={{ ...fieldStyle, maxWidth: '320px' }}
        />
        <div id={`tank-pid-help-${idp}`} style={{ ...subText, textTransform: 'none' }}>
          Your company&rsquo;s ID for this tank, e.g. a lease tank number. Readings sent by MQTT or
          the API use it.
        </div>
      </div>
      <fieldset
        style={{
          borderRadius: '12px',
          backgroundColor: 'var(--color-bg-tertiary)',
          padding: '8px 12px',
        }}
      >
        <legend style={labelStyle}>Where it sits</legend>
        <div style={{ display: 'flex', gap: '16px', fontSize: '13px', marginBottom: '8px' }}>
          {(
            [
              ['WELL', 'Well'],
              ['SITE', 'Site'],
              ['BATTERY', 'Battery'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <input
                type="radio"
                name={`placement-${idp}`}
                value={value}
                checked={placement === value}
                onChange={(): void => setPlacement(value)}
              />
              {label}
            </label>
          ))}
        </div>
        {placement === 'WELL' && (
          <select
            aria-label="Well"
            value={wellId}
            onChange={(e): void => setWellId(e.target.value)}
            style={{ ...fieldStyle, maxWidth: '420px' }}
          >
            <option value="">Choose a well…</option>
            {sortWells(wells).map((w) => (
              <option key={w.wellId} value={w.wellId}>
                {wellLabel(w)}
              </option>
            ))}
          </select>
        )}
        {placement === 'SITE' && (
          <input
            aria-label="Site"
            placeholder="e.g. East yard"
            value={siteLabel}
            maxLength={160}
            onChange={(e): void => setSiteLabel(e.target.value)}
            style={{ ...fieldStyle, maxWidth: '320px' }}
          />
        )}
        {placement === 'BATTERY' &&
          (batteryOptions.length === 0 ? (
            <p style={{ ...subText, textTransform: 'none', margin: 0 }}>
              The company has no active batteries. Add one under Batteries below.
            </p>
          ) : (
            <select
              aria-label="Battery"
              value={batteryId}
              onChange={(e): void => setBatteryId(e.target.value)}
              style={{ ...fieldStyle, maxWidth: '320px' }}
            >
              <option value="">Choose a battery…</option>
              {batteryOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.isActive ? '' : ' (inactive)'}
                </option>
              ))}
            </select>
          ))}
      </fieldset>
      <div>
        <label htmlFor={`tank-std-${idp}`} style={labelStyle}>
          Standard size (optional; fills the sizes below)
        </label>
        <select
          id={`tank-std-${idp}`}
          value={standardId}
          onChange={(e): void => pickSize(e.target.value)}
          style={{ ...fieldStyle, maxWidth: '420px' }}
        >
          <option value="">None</option>
          {sizeOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {standardTankLabel(o)}
            </option>
          ))}
          {standardId !== '' && !sizeOptions.some((o) => o.id === standardId) && (
            <option value={standardId}>A size no longer in the catalog</option>
          )}
        </select>
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {sizeField('tank-capacity', 'Capacity (BBL)', capacity, setCapacity)}
        {sizeField('tank-height', 'Height (in)', height, setHeight)}
        {sizeField('tank-bpi', 'BBL per inch', bpi, setBpi)}
        <div>
          <label htmlFor={`tank-type-${idp}`} style={labelStyle}>
            Tank type
          </label>
          <input
            id={`tank-type-${idp}`}
            placeholder="e.g. GUN BARREL or WATER"
            value={tankType}
            maxLength={60}
            onChange={(e): void => setTankType(e.target.value)}
            style={{ ...fieldStyle, width: '180px' }}
          />
        </div>
      </div>
      {sizesAtCapacity.length > 1 && (
        <p
          role="status"
          style={{ ...subText, textTransform: 'none', margin: 0, color: 'var(--color-warning)' }}
        >
          {capacity.trim()} BBL comes in {sizesAtCapacity.length} standard sizes. Pick one under
          Standard size, or type its BBL per inch:{' '}
          {sizesAtCapacity.map((c) => `${c.name} (${sizeText(c.bblPerInch)} BBL/in)`).join(' or ')}.
        </p>
      )}
      {tank !== null && (
        <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px' }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e): void => setIsActive(e.target.checked)}
          />
          Active
        </label>
      )}
      {error !== null && (
        <p role="alert" style={errorText}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button type="submit" size="sm" style={primaryButton} disabled={save.isPending}>
          {tank === null ? 'Add tank' : 'Save'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ── Tags ─────────────────────────────────────────────────────────────────────

function Tags({
  tenantId,
  tank: t,
}: {
  tenantId: string;
  tank: RegistryTankView;
}): React.JSX.Element {
  const add = useAddCompanyTankTag(tenantId, t.id);
  const [system, setSystem] = React.useState<TankTagSystem>('SCADA');
  const [tag, setTag] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const addTag = (): void => {
    setError(null);
    const value = tag.trim();
    if (value === '') return setError('Type the tag first.');
    add.mutate(
      { system, tag: value },
      {
        onSuccess: () => setTag(''),
        onError: (err) => setError(tankRegistryErrorMessage(err)),
      },
    );
  };

  return (
    <div style={{ marginTop: '8px', fontSize: '13px' }}>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={subText}>Tags</span>
        {t.tags.length === 0 && <span style={subText}>none</span>}
        <ul
          aria-label={`Tags of ${t.name}`}
          style={{ listStyle: 'none', margin: 0, padding: 0, display: 'contents' }}
        >
          {t.tags.map((g) => (
            <TagChip key={g.id} tenantId={tenantId} tag={g} onError={setError} />
          ))}
        </ul>
      </div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '6px' }}>
        <select
          aria-label={`Tag system for ${t.name}`}
          value={system}
          onChange={(e): void => setSystem(e.target.value as TankTagSystem)}
          style={{ ...fieldStyle, width: '110px', minHeight: '34px', padding: '4px 8px' }}
        >
          {TANK_TAG_SYSTEMS.map((s) => (
            <option key={s} value={s}>
              {SYSTEM_LABEL[s]}
            </option>
          ))}
        </select>
        <input
          className="keep-case"
          aria-label={`New tag for ${t.name}`}
          placeholder="Tag or topic"
          value={tag}
          maxLength={200}
          onChange={(e): void => setTag(e.target.value)}
          style={{ ...fieldStyle, width: '220px', minHeight: '34px', padding: '4px 8px' }}
        />
        <Button type="button" variant="outline" size="sm" disabled={add.isPending} onClick={addTag}>
          Add tag
        </Button>
      </div>
      {error !== null && (
        <p role="alert" style={errorText}>
          {error}
        </p>
      )}
    </div>
  );
}

function TagChip({
  tenantId,
  tag: g,
  onError,
}: {
  tenantId: string;
  tag: RegistryTankTagView;
  onError: (message: string | null) => void;
}): React.JSX.Element {
  const remove = useRemoveCompanyTankTag(tenantId, g.id);
  return (
    <li
      style={{
        display: 'inline-flex',
        gap: '6px',
        alignItems: 'center',
        padding: '1px 8px',
        borderRadius: '10px',
        backgroundColor: 'var(--color-bg-tertiary)',
      }}
    >
      <span>
        {SYSTEM_LABEL[g.system]}: {g.tag}
      </span>
      <button
        type="button"
        aria-label={`Remove ${SYSTEM_LABEL[g.system]} tag ${g.tag}`}
        disabled={remove.isPending}
        onClick={(): void => {
          onError(null);
          remove.mutate({}, { onError: (err) => onError(tankRegistryErrorMessage(err)) });
        }}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-danger)',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        ×
      </button>
    </li>
  );
}

// ── Batteries ────────────────────────────────────────────────────────────────

function Batteries({
  tenantId,
  batteries,
  loading,
  failed,
  wells,
}: {
  tenantId: string;
  batteries: readonly BatteryView[];
  loading: boolean;
  failed: boolean;
  wells: readonly CompanyWellOption[];
}): React.JSX.Element {
  const save = useSaveCompanyBattery(tenantId, null);
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const list = [...batteries].sort((a, b) => a.name.localeCompare(b.name));

  const addBattery = (): void => {
    setError(null);
    const value = name.trim();
    if (value === '') return setError('Give the battery a name.');
    save.mutate(
      { name: value },
      {
        onSuccess: () => setName(''),
        onError: (err) => setError(tankRegistryErrorMessage(err)),
      },
    );
  };

  return (
    <div aria-label="Batteries" role="group">
      <h4 style={heading}>Batteries</h4>
      <p style={{ ...subText, textTransform: 'none', margin: '0 0 8px' }}>
        A battery is a group of tanks that serves one or more wells.
      </p>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
        <input
          aria-label="New battery name"
          placeholder="Battery name"
          value={name}
          maxLength={120}
          onChange={(e): void => setName(e.target.value)}
          style={{ ...fieldStyle, maxWidth: '260px' }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={save.isPending}
          onClick={addBattery}
        >
          Add battery
        </Button>
      </div>
      {error !== null && (
        <p role="alert" style={errorText}>
          {error}
        </p>
      )}
      {loading ? (
        <p style={subText}>Loading batteries…</p>
      ) : failed ? (
        <p role="alert" style={errorText}>
          Could not load the batteries.
        </p>
      ) : list.length === 0 ? (
        <p style={{ ...subText, textTransform: 'none' }}>No batteries yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {list.map((b) => (
            <BatteryItem key={b.id} tenantId={tenantId} battery={b} wells={wells} />
          ))}
        </div>
      )}
    </div>
  );
}

function BatteryItem({
  tenantId,
  battery: b,
  wells,
}: {
  tenantId: string;
  battery: BatteryView;
  wells: readonly CompanyWellOption[];
}): React.JSX.Element {
  const save = useSaveCompanyBattery(tenantId, b.id);
  const setWells = useSetCompanyBatteryWells(tenantId, b.id);
  const [renaming, setRenaming] = React.useState(false);
  const [name, setName] = React.useState(b.name);
  const [choosing, setChoosing] = React.useState(false);
  const [chosen, setChosen] = React.useState<ReadonlySet<string>>(new Set());
  const [error, setError] = React.useState<string | null>(null);

  const saveBattery = (nextName: string, isActive: boolean, onSaved?: () => void): void => {
    setError(null);
    if (nextName.trim() === '') return setError('Give the battery a name.');
    save.mutate(
      { name: nextName.trim(), isActive },
      {
        onSuccess: () => onSaved?.(),
        onError: (err) => setError(tankRegistryErrorMessage(err)),
      },
    );
  };

  const saveWells = (): void => {
    setError(null);
    setWells.mutate(
      { wellIds: [...chosen] },
      {
        onSuccess: () => setChoosing(false),
        onError: (err) => setError(tankRegistryErrorMessage(err)),
      },
    );
  };

  const tankCount = `${b.tankCount.toLocaleString()} ${b.tankCount === 1 ? 'tank' : 'tanks'}`;

  return (
    <article
      aria-label={`Battery ${b.name}`}
      style={{ ...itemStyle, opacity: b.isActive ? 1 : 0.75 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {renaming ? (
          <>
            <input
              aria-label="Battery name"
              value={name}
              maxLength={120}
              onChange={(e): void => setName(e.target.value)}
              style={{ ...fieldStyle, maxWidth: '240px' }}
            />
            <Button
              type="button"
              size="sm"
              style={primaryButton}
              disabled={save.isPending}
              onClick={(): void => saveBattery(name, b.isActive, () => setRenaming(false))}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(): void => {
                setRenaming(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <strong style={{ fontSize: '14px' }}>{b.name}</strong>
        )}
        <Pill label={b.isActive ? 'Active' : 'Inactive'} tone={b.isActive ? 'positive' : 'muted'} />
        <span style={subText}>{formatNumber(tankCount)}</span>
        {!renaming && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Rename ${b.name}`}
              onClick={(): void => {
                setName(b.name);
                setRenaming(true);
              }}
            >
              Rename
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`${b.isActive ? 'Deactivate' : 'Reactivate'} ${b.name}`}
              disabled={save.isPending}
              onClick={(): void => saveBattery(b.name, !b.isActive)}
            >
              {b.isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Set wells of ${b.name}`}
              onClick={(): void => {
                setChosen(new Set(b.wells.map((w) => w.wellId)));
                setChoosing(true);
              }}
            >
              Set wells
            </Button>
          </span>
        )}
      </div>
      {choosing ? (
        <fieldset
          style={{
            borderRadius: '12px',
            backgroundColor: 'var(--color-bg-tertiary)',
            padding: '8px 12px',
            marginTop: '8px',
          }}
        >
          <legend style={labelStyle}>Wells {b.name} serves</legend>
          {wells.length === 0 ? (
            <p style={{ ...subText, textTransform: 'none', margin: 0 }}>
              This company has no wells.
            </p>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                maxHeight: '240px',
                overflowY: 'auto',
                fontSize: '13px',
              }}
            >
              {sortWells(wells).map((w) => (
                <label key={w.wellId} style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="checkbox"
                    checked={chosen.has(w.wellId)}
                    onChange={(e): void => {
                      const on = e.target.checked;
                      setChosen((prev) => {
                        const next = new Set(prev);
                        if (on) next.add(w.wellId);
                        else next.delete(w.wellId);
                        return next;
                      });
                    }}
                  />
                  {wellLabel(w)}
                </label>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <Button
              type="button"
              size="sm"
              style={primaryButton}
              disabled={setWells.isPending}
              onClick={saveWells}
            >
              Save wells
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(): void => setChoosing(false)}
            >
              Cancel
            </Button>
          </div>
        </fieldset>
      ) : b.wells.length === 0 ? (
        <p style={{ ...subText, textTransform: 'none', margin: '6px 0 0' }}>No wells yet.</p>
      ) : (
        <ul
          aria-label={`Wells of ${b.name}`}
          style={{ margin: '6px 0 0', paddingLeft: '18px', fontSize: '13px' }}
        >
          {b.wells.map((w) => (
            <li key={w.wellId}>{wellLabel(w)}</li>
          ))}
        </ul>
      )}
      {error !== null && (
        <p role="alert" style={errorText}>
          {error}
        </p>
      )}
    </article>
  );
}
